import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../lib/utils'

interface SheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  className?: string
  children: React.ReactNode
}

/** Right-hand side panel. Same primitive as Dialog, anchored instead of centred. */
function Sheet({ open, onOpenChange, className, children }: SheetProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'bg-scrim fixed inset-0 z-50 duration-150 ease-out',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          data-slot="sheet"
          className={cn(
            'border-border bg-bg-elev shadow-modal fixed inset-y-0 right-0 z-50 flex w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-y-auto overscroll-contain border-l outline-none',
            'duration-150 ease-out',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
            className,
          )}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function SheetHeader({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        'border-border flex shrink-0 items-start justify-between gap-3 border-b px-6 py-4',
        className,
      )}
      {...props}
    >
      <div className="min-w-0">{children}</div>
      <DialogPrimitive.Close
        aria-label="Close"
        className="text-fg-subtle hover:bg-bg-subtle hover:text-fg focus-visible:outline-accent -mt-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md transition-colors duration-100 ease-out outline-none focus-visible:outline-2 focus-visible:outline-offset-1"
      >
        <X className="size-3.5" />
      </DialogPrimitive.Close>
    </div>
  )
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-fg m-0 text-base font-semibold', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-fg-muted mt-1 mb-0 text-sm', className)}
      {...props}
    />
  )
}

function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-body" className={cn('flex-1 px-6 py-5', className)} {...props} />
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        'border-border flex shrink-0 items-center justify-end gap-2 border-t px-6 py-4',
        className,
      )}
      {...props}
    />
  )
}

const SheetClose = DialogPrimitive.Close

export {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetFooter,
  SheetClose,
  type SheetProps,
}
